import { Box, Checkbox, Text } from '@chakra-ui/react';
import { useId } from 'react';
import { useIsAclEnforced } from '../../../hooks/data/useEntityAccess';

interface PrivateToMeFieldProps {
  readonly isChecked: boolean;
  readonly onChange: (isChecked: boolean) => void;
  readonly isDisabled?: boolean;
  /**
   * Name of a private parent. Everything inside it is already private, so the
   * box shows checked and locked, with the reason, instead of offering a
   * choice that changes nothing.
   */
  readonly inheritedFrom?: string;
}

/**
 * "Private to me" on a new collection: the Share dialog's "Just me", applied
 * in the create request itself (`isPrivate`). Hidden when the workspace does
 * not enforce access control, because the setting would protect nothing.
 * The one control every collection-create surface uses.
 */
export function PrivateToMeField({
  isChecked,
  onChange,
  isDisabled,
  inheritedFrom,
}: PrivateToMeFieldProps) {
  const enforced = useIsAclEnforced();
  const descriptionId = useId();
  if (!enforced) return null;
  const locked = inheritedFrom !== undefined;
  return (
    <Box w="full">
      <Checkbox
        isChecked={locked || isChecked}
        isDisabled={isDisabled || locked}
        onChange={(e) => onChange(e.target.checked)}
        aria-describedby={descriptionId}
        alignItems="flex-start"
      >
        <Text as="span" fontSize="sm" color="text.primary">
          Private to me
        </Text>
      </Checkbox>
      <Text id={descriptionId} fontSize="xs" color="text.secondary" pl={6}>
        {locked
          ? `Private because ${inheritedFrom} is private.`
          : 'Only you can see it and what you put in it. You can share it later.'}
      </Text>
    </Box>
  );
}

/**
 * The confirmation for a collection created private. RESTRICTED is what the
 * server makes it where the workspace keeps owners able to see everything,
 * so the copy says so rather than promising "only you".
 */
export function privateCollectionNotice(
  name: string,
  privacyMode: 'PRIVATE' | 'RESTRICTED' | undefined,
): string | null {
  if (privacyMode === 'PRIVATE') return `${name} is private to you.`;
  if (privacyMode === 'RESTRICTED') {
    return `${name} is private to you. Workspace owners can still see it in this workspace.`;
  }
  return null;
}
